import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import { Task, TaskStatus } from "@common/types/task";
import { FinancialDocument } from "@common/types/financialDocument";
import {
    DocumentMigrationError,
    MIGRATION_IMPORT_FAILED_TOAST,
    useSubmitQbdJsonDocumentMigrationMutation,
} from "@api/accountingIntegration.api";
import { DocumentName } from "@components/documents/DocumentName";
import { DocumentsEmptyState } from "@components/documents/DocumentsEmptyState";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FinancialDocumentUploadDropzone } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadDropzone";
import { useFinancialDocumentUpload } from "@components/documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { UploadIcon } from "@icons";
import { useScope } from "@state/scopeContext.lib";
import { QBD_EXTRACTOR_JSON_CONTENT_TYPE } from "@common/constants/financial-documents";
import { QbdDesktopExporterDownloadButton } from "@components/accountingMigration/source";
import { useAdvanceToNextTask } from "@components/tasks/useAdvanceToNextTask";
import { useTaskDocumentLinkHandlers } from "@components/tasks/useTaskDocumentLinkHandlers";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { QbdUploadPathFallbackButton } from "./QbdUploadPathFallbackButton";
import {
    latestLinkedQbdJsonFile,
    SelectedMigrationFile,
} from "./latestLinkedQbdJsonFile";
import { MigrationFinalizeWarningModal } from "@components/accounting/MigrationFinalizeWarningModal";
import styles from "./QbdJsonMigrationUploadTaskBody.module.scss";

const QBD_JSON_ACCEPT_TYPES = [QBD_EXTRACTOR_JSON_CONTENT_TYPE];
const JSON_IMPORT_WARNING_DESCRIPTION =
    "Review these issues before importing. This can't be undone.";

interface QbdJsonMigrationUploadTaskBodyProps {
    selectedTask: Task;
}

export const QbdJsonMigrationUploadTaskBody: React.FC<
    QbdJsonMigrationUploadTaskBodyProps
> = ({ selectedTask }) => {
    const onUploadRef = useRef<
        ((docs: FinancialDocument[]) => void) | undefined
    >(undefined);

    const handleUploadCompleted = useCallback(
        async (uploadedDocuments: FinancialDocument[]) => {
            onUploadRef.current?.(uploadedDocuments);
        },
        [],
    );

    return (
        <FinancialDocumentUploadProvider
            accept={QBD_JSON_ACCEPT_TYPES}
            multiple={false}
            onCompleted={handleUploadCompleted}
        >
            <QbdJsonMigrationUploadBody
                selectedTask={selectedTask}
                onUploadRef={onUploadRef}
            />
        </FinancialDocumentUploadProvider>
    );
};

const QbdJsonMigrationUploadBody: React.FC<{
    selectedTask: Task;
    onUploadRef: React.MutableRefObject<
        ((docs: FinancialDocument[]) => void) | undefined
    >;
}> = ({ selectedTask, onUploadRef }) => {
    const { workspaceId } = useScope();
    const entityId = selectedTask.entityIds?.[0];
    const { error: toastError } = useToaster();
    const advanceToNextTask = useAdvanceToNextTask();
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const { inputProps, open: openFilePicker } = useFinancialDocumentUpload();

    const { handleLinkDocument } = useTaskDocumentLinkHandlers(selectedTask);

    const [uploadedFile, setUploadedFile] =
        useState<SelectedMigrationFile | null>(null);
    const linkedFile = useMemo(
        () => latestLinkedQbdJsonFile(selectedTask.financialDocuments),
        [selectedTask.financialDocuments],
    );
    const selectedFile = uploadedFile ?? linkedFile;
    const [finalizeWarnings, setFinalizeWarnings] = useState<string[]>([]);
    const [showFinalizeWarning, setShowFinalizeWarning] = useState(false);
    const [hasStartedImport, setHasStartedImport] = useState(false);
    const [warnedDocumentId, setWarnedDocumentId] = useState<number | null>(
        null,
    );

    onUploadRef.current = (uploadedDocs: FinancialDocument[]) => {
        const lastDocument = uploadedDocs.at(-1);
        if (lastDocument) {
            setUploadedFile({
                documentId: lastDocument.id,
                fileName: lastDocument.fileName,
            });
            setShowFinalizeWarning(false);
            setWarnedDocumentId(null);
            handleLinkDocument(lastDocument.id).catch(() => undefined);
        }
    };

    const migrateMutation = useSubmitQbdJsonDocumentMigrationMutation({
        workspaceId: workspaceId ?? "",
        entityId: entityId ?? 0,
    });

    const startImport = useCallback(
        ({
            documentId,
            confirmWarnings,
        }: {
            documentId: number;
            confirmWarnings?: boolean;
        }) => {
            if (refuseIfViewOnly()) {
                return;
            }
            migrateMutation.mutate(
                {
                    documentId,
                    confirmWarnings,
                },
                {
                    onSuccess: (body) => {
                        if (body.importStarted) {
                            setShowFinalizeWarning(false);
                            setHasStartedImport(true);
                            return;
                        }
                        if (body.warnings && body.warnings.length > 0) {
                            setWarnedDocumentId(documentId);
                            setFinalizeWarnings(body.warnings);
                            setShowFinalizeWarning(true);
                        }
                    },
                    onError: (error) => {
                        toastError(
                            error instanceof DocumentMigrationError
                                ? error.detail.message
                                : MIGRATION_IMPORT_FAILED_TOAST,
                        );
                    },
                },
            );
        },
        [migrateMutation, toastError, refuseIfViewOnly],
    );

    const handleImport = useCallback(() => {
        if (!selectedFile) {
            return;
        }
        startImport({ documentId: selectedFile.documentId });
    }, [selectedFile, startImport]);

    const isCompleted = selectedTask.status === TaskStatus.DONE;
    const isImportRunning =
        selectedTask.metadata?.migrationImportStatus === "importing";
    const importError =
        selectedTask.metadata?.migrationImportStatus === "failed"
            ? selectedTask.metadata.migrationImportError
            : undefined;
    // The request only enqueues the import, so the button stays busy until the
    // task itself reports the background job.
    const isImporting =
        isImportRunning ||
        migrateMutation.isPending ||
        (hasStartedImport && !isCompleted && !importError);

    const hadRunningImport = useRef(false);
    useEffect(() => {
        if (isImportRunning) {
            hadRunningImport.current = true;
            return;
        }
        if (
            (hadRunningImport.current || migrateMutation.isSuccess) &&
            isCompleted
        ) {
            hadRunningImport.current = false;
            advanceToNextTask(selectedTask);
        }
    }, [
        isImportRunning,
        isCompleted,
        migrateMutation.isSuccess,
        advanceToNextTask,
        selectedTask,
    ]);

    const importTooltip = useViewOnlyTooltipReason(
        selectedFile ? "" : "Upload a migration file first",
    );

    if (!workspaceId || !entityId) {
        return null;
    }

    if (isCompleted) {
        return (
            <div className={styles.container}>
                <EmptyState
                    header="Migration file imported"
                    body="The QuickBooks Desktop migration file has been imported."
                />
            </div>
        );
    }

    if (isImportRunning) {
        return (
            <div className={styles.container}>
                <EmptyState
                    header="Importing your migration file"
                    body="This can take a few minutes. You can leave this page — the task updates itself when the import finishes."
                />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <input {...inputProps} />

            <VStack
                gap={16}
                align="center"
                paddingBlock={64}
                className={styles.uploadPrompt}
            >
                <VStack gap={8} className={styles.uploadCopy}>
                    <div className={styles.uploadHeader}>
                        {importError
                            ? "Import failed"
                            : "Upload your QuickBooks Desktop JSON backup file"}
                    </div>
                    {importError ? (
                        <p>{importError}</p>
                    ) : (
                        <p className={styles.instructions}>
                            Use <QbdDesktopExporterDownloadButton /> to extract
                            entity backup as a JSON
                        </p>
                    )}
                </VStack>
            </VStack>

            <FinancialDocumentUploadDropzone
                dropzoneClassName={classNames(styles.dropzone, {
                    [styles.dropzoneDisabled]: isImporting,
                })}
                cover={
                    <div className={styles.dropCover}>
                        <UploadIcon />
                        <span>Drop files to upload</span>
                    </div>
                }
            >
                <DocumentsEmptyState
                    header={
                        selectedFile ? (
                            <DocumentName
                                fileName={selectedFile.fileName}
                                icon
                                documentId={selectedFile.documentId}
                            />
                        ) : (
                            "Upload or drag and drop your JSON file"
                        )
                    }
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={openFilePicker}
                        disabled={isImporting}
                        leftIcon={<UploadIcon />}
                    >
                        Upload file
                    </Button>
                </DocumentsEmptyState>
            </FinancialDocumentUploadDropzone>

            <CommandIsland sticky className={styles.commandIsland}>
                <div className={styles.commandIslandSpacer} />
                <div className={styles.commandBarActions}>
                    <QbdUploadPathFallbackButton
                        selectedTask={selectedTask}
                        source="qbdt_json"
                        disabled={isImporting}
                    />
                    <Button
                        variant="primary"
                        size="md"
                        onClick={openFilePicker}
                        disabled={isImporting}
                        leftIcon={<UploadIcon />}
                    >
                        Upload file
                    </Button>
                    <Tooltip content={importTooltip}>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            onClick={handleImport}
                            loading={isImporting}
                            disabled={
                                !selectedFile || isImporting || isViewOnly
                            }
                        >
                            Import
                        </ButtonWithLoader>
                    </Tooltip>
                </div>
            </CommandIsland>
            <MigrationFinalizeWarningModal
                show={showFinalizeWarning}
                warnings={finalizeWarnings}
                description={JSON_IMPORT_WARNING_DESCRIPTION}
                confirmLabel="Import anyway"
                onCancel={() => setShowFinalizeWarning(false)}
                onConfirm={() => {
                    if (warnedDocumentId == null) {
                        return;
                    }
                    startImport({
                        documentId: warnedDocumentId,
                        confirmWarnings: true,
                    });
                }}
                isConfirming={migrateMutation.isPending}
            />
        </div>
    );
};
